// Who holds an agent question: the shared owner chip and picker over the Team document's
// members, each with the open questions they hold. Assigning tells them in #agent-feedback;
// anyone may still answer. See docs/Y2026-Q4/design-feedback-assignment.md.
import { useContext } from "react";

import {
  type FeedbackQuestion,
  needsOperator,
  teamName,
  useAssignFeedback,
  useOpenFeedback,
} from "../api/hooks/useAgentFeedback";
import { UserContext } from "../auth";
import { AssigneeChip, AssigneeMenu, PersonMark } from "./AssigneePicker";

export const FeedbackAssignChip = ({
  question,
  team,
}: {
  question: FeedbackQuestion;
  team: Record<string, string>;
}) => {
  const { user } = useContext(UserContext);
  const viewer = (user?.email ?? "").toLowerCase();
  const assign = useAssignFeedback();
  // The same query as the badge: one read, however many rows show a chip.
  const open = useOpenFeedback(true, viewer);
  const held = new Map<string, number>();
  for (const q of open.data?.questions ?? [])
    if (q.assignee && needsOperator(q))
      held.set(q.assignee.toLowerCase(), (held.get(q.assignee.toLowerCase()) ?? 0) + 1);
  const current = question.assignee ?? null;
  const label = current ? teamName(team, current) : "Assign";
  const people = Object.entries(team).sort(([a, an], [b, bn]) =>
    a.toLowerCase() === viewer ? -1 : b.toLowerCase() === viewer ? 1 : an.localeCompare(bn),
  );
  const pick = (assignee: string | null, close: () => void) => {
    close();
    if (assignee === current) return;
    assign.mutate({ question_id: question.question_id, assignee, expected: current });
  };
  return (
    <AssigneeChip
      label={label}
      mark={<PersonMark name={label} tone={current ? "person" : "nobody"} />}
      assigned={current !== null}
      ariaLabel={current ? `Assigned to ${label}, change` : "Assign someone"}
      title={undefined}
      disabled={assign.isLoading}
      error={assign.isError ? assign.error.message : null}
      menu={(close) => (
        <AssigneeMenu
          sections={[
            people.map(([email, name]) => ({
              key: email,
              mark: <PersonMark name={name} tone="person" />,
              label: `${name}${email.toLowerCase() === viewer ? " (you)" : ""}`,
              aside: `${held.get(email.toLowerCase()) ?? 0} open`,
              checked: current?.toLowerCase() === email.toLowerCase(),
              onPick: () => pick(email, close),
            })),
          ]}
          onClear={current !== null ? () => pick(null, close) : null}
          foot="Recorded on the question and posted to #agent-feedback, tagging them. Anyone can still answer it."
        />
      )}
    />
  );
};
